import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, FileDown, X, Code2, ChevronDown, Package, Monitor, Wrench, AlertTriangle, ExternalLink, Boxes } from 'lucide-react';
import { productsApi } from '../../api/endpoints/products';
import { AppFulfillmentSection } from './AppFulfillmentSection';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Toggle } from '../../components/ui/Toggle';
import { useToast } from '../../components/ui/Alert';
import { RichTextEditor } from '../../components/ui/RichTextEditor';
import { AiWritingAssistant } from '../../components/ui/AiWritingAssistant';
import { CURRENCIES } from '../../lib/constants';
import { totalUploadBytes, MAX_UPLOAD_BYTES } from '../../lib/imageUpload';
import { useStore } from '../../contexts/StoreContext';
import { ImagesSection, CategoriesSection, SeoSection, VariantsSection, CustomFieldsSection, DigitalFileSection, TagsSection, VideoSection } from './ProductFormSections';
import type { CustomField, FieldEvent, ProductSeo, VariantType, Variant } from '../../types/product';
import type { ProductFormHandle, ProductFormPrefill } from '../../contexts/ProductFormContext';

// A cleared <input type="number"> hands react-hook-form '' , and z.coerce turns
// that into 0 — which is a real value, not "empty". That silently resubmitted 0
// for untouched optional fields, and for `weight` the API rejects 0 outright
// (it validates .positive()), so one blank weight box 400'd the whole save.
// Normalize empty to undefined so these fields are simply omitted.
const optionalNumber = (inner: z.ZodTypeAny) =>
  z.preprocess((v) => (v === '' || v === null ? undefined : v), inner);

const schema = z.object({
  title: z.string().min(1, 'Required'),
  description: z.string().optional(),
  price: z.coerce.number().min(0),
  compareAtPrice: optionalNumber(z.coerce.number().min(0).optional()),
  salePrice: optionalNumber(z.coerce.number().min(0).optional()),
  productType: z.enum(['physical', 'digital', 'service', 'code', 'file', '']),
  accessibility: z.enum(['public', 'private', 'password', 'archived']),
  quantity: optionalNumber(z.coerce.number().int().min(0).optional()),
  sku: z.string().optional(),
  weight: optionalNumber(z.coerce.number().positive().optional()),
});

type FormValues = z.infer<typeof schema>;

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId?: string | null; // null = create, string = edit
}

export const ProductFormModal = forwardRef<ProductFormHandle, ProductFormModalProps>(function ProductFormModal({ isOpen, onClose, productId }, ref) {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentStore } = useStore();
  const isEdit = !!productId;

  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState('');
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [seo, setSeo] = useState<ProductSeo>({});
  const [unlimitedQty, setUnlimitedQty] = useState(false);
  const [digitalFile, setDigitalFile] = useState<File | null>(null);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showFulfillment, setShowFulfillment] = useState(false);
  const [productCodes, setProductCodes] = useState('');
  const [variantTypes, setVariantTypes] = useState<VariantType[]>([]);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [fieldsEvents, setFieldsEvents] = useState<FieldEvent[]>([]);
  const [shortDescription, setShortDescription] = useState('');
  const [promotionTitle, setPromotionTitle] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [weightUnit, setWeightUnit] = useState('kg');
  const [minQuantity, setMinQuantity] = useState<number | undefined>();
  const [maxQuantity, setMaxQuantity] = useState<number | undefined>();
  const [showQuantity, setShowQuantity] = useState(true);

  // Mirror variantTypes into a ref so the imperative handle can resolve
  // option-name combinations to internal ids without taking variantTypes as a
  // useImperativeHandle dep (which would re-create the handle every render).
  const variantTypesRef = useRef<VariantType[]>(variantTypes);
  useEffect(() => {
    variantTypesRef.current = variantTypes;
  }, [variantTypes]);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { productType: '' as any, accessibility: 'public', price: 0 },
  });

  // Load product data for edit
  const { data: product } = useQuery({
    queryKey: ['product', storeId, productId],
    queryFn: () => productsApi.get(storeId!, productId!),
    enabled: !!storeId && !!productId && isOpen,
  });

  useEffect(() => {
    if (product && isEdit) {
      // Storefront and legacy view prefer formattedDescription (HTML) over plain description.
      // Load HTML version into the rich-text editor when present so users keep formatting.
      const richDesc = product.formattedDescription || product.description || '';
      reset({
        title: product.title,
        description: richDesc,
        // `price` from the API is already the price customers pay and
        // `compareAtPrice` the crossed-out original, so those two fully describe
        // the discount. `salePrice` is deliberately left empty: pre-filling it
        // meant an untouched form sent the old sale price back alongside the new
        // price, and the old one won — the price edit appeared to do nothing.
        price: product.price,
        compareAtPrice: product.compareAtPrice ?? undefined,
        salePrice: undefined,
        productType: product.productType,
        accessibility: product.accessibility,
        quantity: product.quantity ?? undefined,
        sku: product.sku ?? '',
        weight: product.weight ?? undefined,
      });
      setDescription(richDesc);
      setCurrency((product as any).currency ?? '');
      setExistingImages(product.images ?? []);
      setVideoUrl(product.videoUrl ?? '');
      setCategories(product.categories ?? []);
      setSeo(product.seo ?? {});
      setUnlimitedQty(product.quantity == null);
      setVariantTypes(product.variantTypes ?? []);
      setVariants(product.variants ?? []);
      setCustomFields(product.fields ?? []);
      setFieldsEvents(product.fieldsEvents ?? []);
      setShortDescription((product as any).shortDescription ?? '');
      setPromotionTitle((product as any).promotionTitle ?? '');
      setTags(product.tags ?? []);
      setWeightUnit((product as any).weightUnit ?? 'kg');
      setMinQuantity(product.minQuantity);
      setMaxQuantity(product.maxQuantity);
      setShowQuantity(product.showQuantity ?? true);
    }
  }, [product, isEdit, reset]);

  // Reset on the falling edge (modal closed) rather than on open. Resetting on
  // open would wipe values the AI assistant just prefilled in the same turn,
  // because both setState calls batch into a single render and the reset
  // effect fires after. Edit-mode population (the effect above) keys on
  // `product` so it still re-fills when re-opening an edit.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      reset({ productType: '' as any, accessibility: 'public', price: 0 });
      setDescription('');
      setCurrency('');
      setExistingImages([]);
      setVideoUrl('');
      setCategories([]);
      setSeo({});
      setUnlimitedQty(false);
      setDigitalFile(null);
      setShowAdvanced(false);
      setProductCodes('');
      setVariantTypes([]);
      setVariants([]);
      setCustomFields([]);
      setFieldsEvents([]);
      setShortDescription('');
      setPromotionTitle('');
      setTags([]);
      setWeightUnit('kg');
      setMinQuantity(undefined);
      setMaxQuantity(undefined);
      setShowQuantity(true);
      setShowFulfillment(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, reset]);

  // Expose an imperative handle so the AI assistant (or any consumer with a
  // ref) can patch fields, push variant types, or attach categories on the
  // currently-open form.
  useImperativeHandle(ref, (): ProductFormHandle => ({
    setFields: (values: ProductFormPrefill) => {
      if (values.title !== undefined) setValue('title', values.title, { shouldDirty: true });
      if (values.price !== undefined) setValue('price', values.price, { shouldDirty: true });
      if (values.salePrice !== undefined) setValue('salePrice', values.salePrice, { shouldDirty: true });
      if (values.compareAtPrice !== undefined) setValue('compareAtPrice', values.compareAtPrice, { shouldDirty: true });
      if (values.sku !== undefined) setValue('sku', values.sku, { shouldDirty: true });
      if (values.weight !== undefined) setValue('weight', values.weight, { shouldDirty: true });
      if (values.productType !== undefined) setValue('productType', values.productType, { shouldDirty: true });
      if (values.accessibility !== undefined) setValue('accessibility', values.accessibility, { shouldDirty: true });
      if (values.quantity !== undefined) {
        setValue('quantity', values.quantity, { shouldDirty: true });
        setUnlimitedQty(false);
      }
      if (values.unlimitedQuantity === true) setUnlimitedQty(true);
      if (values.description !== undefined) setDescription(values.description);
    },
    addVariantType: ({ name, options }) => {
      const cleaned = (options || []).map((s) => String(s).trim()).filter(Boolean);
      const stamp = Date.now();
      const rand = Math.random().toString(36).slice(2, 6);
      const newType: VariantType = {
        id: `vt_${stamp}_${rand}`,
        name: String(name || '').trim(),
        displayType: 'dropdown',
        options: cleaned.map((n, i) => ({
          id: `vo_${stamp}_${i}_${Math.random().toString(36).slice(2, 4)}`,
          name: n,
        })),
      };
      setVariantTypes((prevTypes) => {
        const nextTypes = [...prevTypes, newType];
        // Regenerate the cartesian product of all variant combinations.
        // Mirrors VariantsSection.regenerateCombinations but reads previous
        // variants via the functional setter so we don't capture stale state.
        setVariants((prevVariants) => {
          const validTypes = nextTypes.filter((t) => t.options.length > 0);
          if (validTypes.length === 0) return [];
          let combos: Record<string, string>[] = [{}];
          for (const vt of validTypes) {
            const next: Record<string, string>[] = [];
            for (const combo of combos) {
              for (const opt of vt.options) {
                if (!opt.name.trim()) continue;
                next.push({ ...combo, [vt.id]: opt.id });
              }
            }
            combos = next;
          }
          return combos.map((combination) => {
            const key = JSON.stringify(combination);
            const existing = prevVariants.find((v) => JSON.stringify(v.combination) === key);
            return (
              existing ?? {
                id: `v_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                combination,
                isActive: true,
              }
            );
          });
        });
        return nextTypes;
      });
      return newType;
    },
    addCategory: (categoryId: string) => {
      const id = String(categoryId || '').trim();
      if (!id) return;
      setCategories((prev) => (prev.includes(id) ? prev : [...prev, id]));
    },
    setVariantOverride: ({ combination, price, priceModifier, quantity, sku, isActive }) => {
      // Map (typeName -> optionName) input to (typeId -> optionId) the schema
      // uses internally. Reads via ref to stay in sync without re-creating the handle.
      const types = variantTypesRef.current;
      const idCombo: Record<string, string> = {};
      for (const [typeName, optionName] of Object.entries(combination || {})) {
        const type = types.find((t) => t.name === typeName);
        if (!type) return false;
        const opt = type.options.find((o) => o.name === optionName);
        if (!opt) return false;
        idCombo[type.id] = opt.id;
      }
      const expectedKeys = Object.keys(idCombo);
      if (expectedKeys.length === 0) return false;

      let matched = false;
      setVariants((prev) => {
        const idx = prev.findIndex((v) => {
          const c = v.combination || {};
          if (Object.keys(c).length !== expectedKeys.length) return false;
          return expectedKeys.every((k) => c[k] === idCombo[k]);
        });
        if (idx < 0) return prev;
        matched = true;
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          ...(price !== undefined ? { price } : {}),
          ...(priceModifier !== undefined ? { priceModifier } : {}),
          ...(quantity !== undefined ? { quantity } : {}),
          ...(sku !== undefined ? { sku } : {}),
          ...(isActive !== undefined ? { isActive } : {}),
        };
        return next;
      });
      return matched;
    },
  }), [setValue]);

  const mutation = useMutation({
    mutationFn: (data: FormValues) => {
      // Rich-text editor produces HTML. Storefront/legacy view read
      // `formattedDescription || description`, so persist HTML there and a
      // plain-text strip in `description` for SEO meta + 5000-char limit.
      const html = description || '';
      const plain = html ? html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 5000) : '';
      const payload: Record<string, unknown> = {
        ...data,
        description: plain || undefined,
        formattedDescription: html || undefined,
        // On edit send null (not undefined) when emptied: undefined is dropped
        // from the JSON body, so the backend never sees the field and the old
        // value survives. null is what its schema turns into a $unset.
        shortDescription: shortDescription || (isEdit ? null : undefined),
        promotionTitle: promotionTitle || (isEdit ? null : undefined),
        currency: currency || undefined,
        // Same null-on-edit rule as the fields above: an omitted key leaves the
        // old link in place, so clearing the box has to send null to unset it.
        videoUrl: videoUrl.trim() || (isEdit ? null : undefined),
        categories,
        // Same reason as above: on edit these must be sent as an empty array /
        // null to actually clear, since an omitted key leaves the old value.
        tags: tags.length > 0 ? tags : (isEdit ? [] : undefined),
        weightUnit,
        minQuantity: minQuantity ?? (isEdit ? null : undefined),
        maxQuantity: maxQuantity ?? (isEdit ? null : undefined),
        showQuantity,
        seo,
      };
      if (unlimitedQty) payload.quantity = 'Infinity';
      if (variantTypes.length > 0) {
        payload.hasVariants = true;
        payload.variantTypes = variantTypes;
        payload.variants = variants;
      }
      payload.fields = customFields;
      if (fieldsEvents.length > 0) payload.fieldsEvents = fieldsEvents;
      // Code-type products: the codes textarea only renders on create (edit has
      // its own codes page), so this has to ride along with the create payload.
      if (!isEdit && productCodes.trim()) {
        payload.productCodes = productCodes.split(/\r?\n/).map(c => c.trim()).filter(Boolean);
      }

      // Images are CDN filenames now, so they weigh nothing. Variant images
      // saved before that change are still inline data URLs on the product, and
      // an edit round-trips them, so that is the one thing that can still
      // overflow the server's 11 MB body. A bare 413 gave the user nothing to
      // act on; say what is actually wrong.
      if (totalUploadBytes(variants.flatMap(v => v.images ?? [])) > MAX_UPLOAD_BYTES) {
        throw new Error(t('products.imagesTooLarge', 'These images are too large to save at once. Remove a few and save again.'));
      }

      // One ordered list of filenames. The first entry becomes previewImage and
      // the rest attachments, which is what makes drag-to-front work.
      payload.images = existingImages;

      if (isEdit) {
        // Attach original quantity for atomic update
        if (product && payload.quantity !== undefined) {
          payload._originalQuantity = product.quantity ?? null;
        }
        return productsApi.update(storeId!, productId!, payload as any);
      } else {
        return productsApi.create(storeId!, payload as any);
      }
    },
    onSuccess: async (result: any) => {
      // Upload digital file for new products
      if (!isEdit && digitalFile && result) {
        const newProductId = result.productId || result._id;
        if (newProductId) {
          try {
            // Upload through the backend (same-origin); it forwards to the CDN
            // and links the file to the product.
            await productsApi.uploadDigitalFile(storeId!, newProductId, digitalFile);
          } catch {
            toast('warning', t('products.digitalFileUploadFailed', 'Product created, but file upload failed.'));
          }
        }
      }
      queryClient.invalidateQueries({ queryKey: ['products', storeId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', storeId] });
      if (isEdit) queryClient.invalidateQueries({ queryKey: ['product', storeId, productId] });
      toast('success', t(isEdit ? 'products.updateSuccess' : 'products.createSuccess'));
      onClose();
    },
    // A guard thrown before the request (an oversized image payload) carries a
    // message worth showing; an axios failure only carries "status code 400".
    onError: (err: any) => toast('error', err?.response ? t('common.error') : (err?.message || t('common.error'))),
  });

  // Already uploaded by ImagesSection; this only records the CDN basename.
  const addImage = useCallback((fileName: string) => setExistingImages(prev => [...prev, fileName]), []);

  const toggleUnlimited = () => {
    setUnlimitedQty(!unlimitedQty);
    if (!unlimitedQty) setValue('quantity', undefined);
  };

  const productTypes = [
    { value: 'physical', label: t('products.physical', 'Physical'), icon: <Package className="h-5 w-5" /> },
    { value: 'digital', label: t('products.digital', 'Digital'), icon: <Monitor className="h-5 w-5" /> },
    { value: 'code', label: t('products.code', 'Code'), icon: <Code2 className="h-5 w-5" /> },
    { value: 'file', label: t('products.file', 'File'), icon: <FileDown className="h-5 w-5" /> },
    { value: 'service', label: t('products.service', 'Service'), icon: <Wrench className="h-5 w-5" /> },
  ];

  const currentProductType = watch('productType');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t(isEdit ? 'products.editProduct' : 'products.addProduct')}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button isLoading={mutation.isPending} disabled={!currentProductType} onClick={handleSubmit(data => mutation.mutate(data))}>{t('common.save')}</Button>
        </>
      }
    >
      <form onSubmit={e => e.preventDefault()} className="space-y-4">
        {/* Product type selector */}
        <div>
          <label className="block text-sm text-text-secondary mb-2">{t('products.productType')}</label>
          <div className="flex gap-2 flex-wrap">
            {productTypes.map((pt) => (
              <button
                key={pt.value}
                type="button"
                onClick={() => setValue('productType', pt.value as any)}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${
                  currentProductType === pt.value
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-border-default text-text-secondary hover:border-accent/40 hover:text-text-primary'
                }`}
              >
                {pt.icon}
                {pt.label}
              </button>
            ))}
          </div>
          {/* Warning when changing type on existing product */}
          {isEdit && product && currentProductType !== product.productType && (
            <div className="flex items-start gap-2 rounded-lg bg-warning/10 border border-warning/20 px-3 py-2 mt-2">
              <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
              <p className="text-xs text-warning">{t('products.typeChangeWarning')}</p>
            </div>
          )}
        </div>

        {/* Everything below only shows after type is selected */}
        {currentProductType && (
          <>
            {/* Images - only in create mode; edit uses image popup from card */}
            {!isEdit && (
              <ImagesSection
                images={existingImages}
                newImages={[]}
                onAddImage={addImage}
                onRemoveExisting={(i) => setExistingImages(prev => prev.filter((_, idx) => idx !== i))}
                onRemoveNew={() => {}}
              />
            )}

            {/* Title */}
            <Input id="title" label={t('products.productName')} required error={errors.title?.message} {...register('title')} />

            {/* Price */}
            <Input id="price" label={t('common.price')} type="number" step="0.01" required error={errors.price?.message} {...register('price')} />

            {/* Description - shown directly in edit mode, hidden in create (advanced) */}
            {isEdit && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm text-text-secondary">{t('common.description')}</label>
                  <AiWritingAssistant
                    currentContent={description}
                    productTitle={watch('title')}
                    onContentGenerated={setDescription}
                  />
                </div>
                <RichTextEditor
                  value={description}
                  onChange={setDescription}
                  placeholder={t('products.descriptionPlaceholder', 'Describe your product...')}
                />
              </div>
            )}

            {/* Product video — rendered in both modes (ImagesSection above is
                create-only, and edit uploads images from the card popup). */}
            <VideoSection value={videoUrl} onChange={setVideoUrl} />

            {/* Variants - shown directly in edit mode for physical/digital */}
            {isEdit && (currentProductType === 'physical' || currentProductType === 'digital') && (
              <VariantsSection
                variantTypes={variantTypes}
                variants={variants}
                onChangeTypes={setVariantTypes}
                onChangeVariants={setVariants}
              />
            )}

            {/* Codes section - textarea for create, link to codes page for edit */}
            {currentProductType === 'code' && !isEdit && (
              <div className="rounded-lg border border-border-default bg-bg-tertiary p-4 space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Code2 className="h-4 w-4 text-accent" />
                  {t('products.productCodes')}
                </h3>
                <p className="text-xs text-text-muted">{t('products.codesHint')}</p>
                <textarea
                  value={productCodes}
                  onChange={(e) => setProductCodes(e.target.value)}
                  placeholder={t('products.codesPlaceholder', 'CODE-001\nCODE-002\nCODE-003')}
                  rows={5}
                  className="w-full rounded-lg border border-border-default bg-bg-field px-3 py-2 text-sm font-mono text-text-primary outline-none focus:border-accent placeholder:text-text-muted/50"
                />
                {productCodes.trim() && (
                  <p className="text-xs text-text-muted">
                    {t('products.codesCount', '{{count}} codes', { count: productCodes.trim().split('\n').filter(Boolean).length })}
                  </p>
                )}
              </div>
            )}
            {currentProductType === 'code' && isEdit && productId && (
              <div className="rounded-lg border border-border-default bg-bg-tertiary p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <Code2 className="h-4 w-4 text-accent" />
                    {t('products.productCodes')}
                  </h3>
                  <span className="text-xs text-text-muted">{t('common.quantity')}: {product?.quantity ?? 0}</span>
                </div>
                <button
                  type="button"
                  onClick={() => { onClose(); navigate(`/${storeId}/products/${productId}/codes`); }}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-accent/30 bg-accent/5 px-4 py-2.5 text-sm font-medium text-accent transition-colors hover:bg-accent/10"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t('products.manageCodes', 'Manage Codes')}
                </button>
              </div>
            )}

            {/* Digital file upload - file type only, create only */}
            {currentProductType === 'file' && !isEdit && (
              <div className="rounded-lg border border-border-default bg-bg-tertiary p-4 space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <FileDown className="h-4 w-4 text-accent" />
                  {t('products.digitalFile', 'Digital File')}
                </h3>
                {digitalFile ? (
                  <div className="flex items-center gap-3 rounded-lg border border-border-default bg-bg-secondary p-3">
                    <span className="text-sm font-medium truncate flex-1">{digitalFile.name}</span>
                    <span className="text-xs text-text-muted">{(digitalFile.size / 1024 / 1024).toFixed(2)} MB</span>
                    <button type="button" onClick={() => setDigitalFile(null)} className="rounded-md p-1 text-text-muted hover:text-danger hover:bg-bg-hover">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 rounded-lg border border-dashed border-border-default px-4 py-3 text-sm text-text-muted hover:border-accent/50 hover:text-accent transition-colors cursor-pointer">
                    <Upload className="h-4 w-4" />
                    {t('products.uploadDigitalFile', 'Upload Digital File')}
                    <input type="file" className="hidden" onChange={(e) => e.target.files?.[0] && setDigitalFile(e.target.files[0])} />
                  </label>
                )}
                <p className="text-xs text-text-muted">{t('products.digitalFileCreateHint', 'The file will be uploaded automatically after the product is created.')}</p>
              </div>
            )}

            {/* Digital file - file type, edit: upload/replace the file */}
            {currentProductType === 'file' && isEdit && productId && (
              <div className="rounded-lg border border-border-default bg-bg-tertiary p-4 space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <FileDown className="h-4 w-4 text-accent" />
                  {t('products.digitalFile', 'Digital File')}
                </h3>
                <DigitalFileSection
                  storeId={storeId!}
                  productId={productId}
                  currentFile={product?.digitalFiles?.[0] ?? product?.digitalFile}
                  onFileUploaded={() => {
                    queryClient.invalidateQueries({ queryKey: ['product', storeId, productId] });
                    queryClient.invalidateQueries({ queryKey: ['products', storeId] });
                    toast('success', t('common.success'));
                  }}
                />
              </div>
            )}

            {/* Advanced options */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                {t('products.advancedOptions')}
              </button>
              {showAdvanced && (
                <div className="mt-3 space-y-4">
                  {/* Short description + promotion badge */}
                  <Input
                    id="shortDescription"
                    label={t('products.shortDescription', 'Short Description')}
                    value={shortDescription}
                    onChange={(e) => setShortDescription(e.target.value.slice(0, 120))}
                    placeholder={t('products.shortDescriptionPlaceholder', 'Shown below the title (max 120 chars)')}
                  />
                  <Input
                    id="promotionTitle"
                    label={t('products.promotionTitle', 'Promotion Badge')}
                    value={promotionTitle}
                    onChange={(e) => setPromotionTitle(e.target.value.slice(0, 30))}
                    placeholder={t('products.promotionTitlePlaceholder', 'Shown on product card (max 30 chars)')}
                  />

                  {/* Description - in advanced for create mode only */}
                  {!isEdit && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-sm text-text-secondary">{t('common.description')}</label>
                        <AiWritingAssistant
                          currentContent={description}
                          productTitle={watch('title')}
                          onContentGenerated={setDescription}
                        />
                      </div>
                      <RichTextEditor
                        value={description}
                        onChange={setDescription}
                        placeholder={t('products.descriptionPlaceholder', 'Describe your product...')}
                      />
                    </div>
                  )}

                  {/* Extra pricing */}
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Input id="compareAt" label={t('products.compareAtPrice')} type="number" step="0.01" {...register('compareAtPrice')} />
                    <Input id="sale" label={t('products.salePrice')} type="number" step="0.01" {...register('salePrice')} />
                    <Select
                      id="currency"
                      label={t('products.currency', 'Currency')}
                      options={[
                        { value: '', label: currentStore?.currency?.toUpperCase() || 'SAR' },
                        ...CURRENCIES.map((c) => ({ value: c, label: c })),
                      ]}
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                    />
                  </div>

                  {/* Categories */}
                  <CategoriesSection selected={categories} onChange={setCategories} />

                  {/* Tags */}
                  <TagsSection tags={tags} onChange={setTags} />

                  {/* Inventory - quantity for everything except code (codes manage their own stock) */}
                  {currentProductType !== 'code' && (
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div>
                        <label htmlFor="qty" className="block text-sm text-text-secondary mb-1.5">{t('common.quantity')}</label>
                        <div className="flex gap-2">
                          <input id="qty" type="number" disabled={unlimitedQty} className="w-full rounded-lg border border-border-default bg-bg-field px-3 py-2 text-sm text-text-primary outline-none focus:border-accent disabled:opacity-50" {...register('quantity')} />
                          <button type="button" onClick={toggleUnlimited} title={t('products.unlimited')} className={`shrink-0 rounded-lg border px-3 py-2 text-sm font-bold transition-colors ${unlimitedQty ? 'border-accent bg-accent/15 text-accent' : 'border-border-default text-text-muted hover:border-accent/50'}`}>∞</button>
                        </div>
                      </div>
                      {(currentProductType === 'physical' || !currentProductType) && (
                        <>
                          <Input id="sku" label={t('products.sku')} {...register('sku')} />
                          <div>
                            <label htmlFor="weight" className="block text-sm text-text-secondary mb-1.5">{t('products.weight')}</label>
                            <div className="flex gap-2">
                              <input id="weight" type="number" step="0.1" className="w-full rounded-lg border border-border-default bg-bg-field px-3 py-2 text-sm text-text-primary outline-none focus:border-accent" {...register('weight')} />
                              <select
                                value={weightUnit}
                                onChange={(e) => setWeightUnit(e.target.value)}
                                className="shrink-0 rounded-lg border border-border-default bg-bg-field px-2 py-2 text-sm text-text-primary outline-none focus:border-accent"
                              >
                                <option value="kg">kg</option>
                                <option value="g">g</option>
                                <option value="lb">lb</option>
                                <option value="oz">oz</option>
                              </select>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Variants - in advanced for create mode only */}
                  {!isEdit && (currentProductType === 'physical' || currentProductType === 'digital') && (
                    <VariantsSection
                      variantTypes={variantTypes}
                      variants={variants}
                      onChangeTypes={setVariantTypes}
                      onChangeVariants={setVariants}
                    />
                  )}

                  {/* Order quantity limits + availability display */}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input
                      label={t('products.minQuantity')}
                      type="number"
                      value={minQuantity ?? ''}
                      onChange={(e) => setMinQuantity(e.target.value ? Number(e.target.value) : undefined)}
                    />
                    <Input
                      label={t('products.maxQuantity')}
                      type="number"
                      value={maxQuantity ?? ''}
                      onChange={(e) => setMaxQuantity(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </div>
                  <Toggle
                    label={t('products.showQuantity', 'Show available quantity to customers')}
                    checked={showQuantity}
                    onChange={setShowQuantity}
                  />

                  {/* Accessibility */}
                  <Select id="access" label={t('products.accessibility')} options={[
                    { value: 'public', label: t('products.public') },
                    { value: 'private', label: t('products.private') },
                  ]} {...register('accessibility')} />

                  {/* Custom input fields shown to the customer at checkout */}
                  <CustomFieldsSection
                    fields={customFields}
                    onChange={setCustomFields}
                    fieldsEvents={fieldsEvents}
                    onChangeEvents={setFieldsEvents}
                  />

                  {/* SEO */}
                  <SeoSection seo={seo} onChange={setSeo} inline />
                </div>
              )}
            </div>

            {/* App fulfillment - generic, collapsed, quiet; edit mode only */}
            {isEdit && productId && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowFulfillment(!showFulfillment)}
                  className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${showFulfillment ? 'rotate-180' : ''}`} />
                  <Boxes className="h-4 w-4" />
                  {t('fulfillment.section', 'App fulfillment')}
                </button>
                {showFulfillment && (
                  <AppFulfillmentSection
                    storeId={storeId!}
                    productId={productId}
                    fields={customFields}
                  />
                )}
              </div>
            )}
          </>
        )}
      </form>
    </Modal>
  );
});
